import { APIResponse } from 'types/Request';
import { getCookie } from 'helpers';
import config from 'config';

export interface ContentItem {
  key: string;
  content: string;
}

async function getContent(key: string): Promise<ContentItem | null> {
  const url = new URL(`${ config.baseUrl }/contents/${key}`);

  const response = await fetch(url.toString(), {
    method: 'GET',
    headers: {
      'Content-Type': 'application/json',
      Authorization: getCookie('accessToken'),
    },
  });

  if (response.status !== 200) {
    console.error(response.statusText);
    return null;
  }

  const content = await response.json() as APIResponse<ContentItem>;
  if (content.error || ! content.payload) {
    console.error(content.error);
    return null;
  }

  return content.payload;
}

export async function getBillingRule(): Promise<ContentItem | null> {
  return getContent('billing_rules');
}

export async function getInsurerModule(): Promise<ContentItem | null> {
  return getContent('insurer_modules');
}

export async function getIOMAModule(): Promise<ContentItem | null> {
  return getContent('ioma_modules');
}

export async function getSpecialtyModule(): Promise<ContentItem | null> {
  return getContent('specialty_modules');
}

export async function getOtherModule(): Promise<ContentItem | null> {
  return getContent('other_modules');
}

export async function getInsurerRegulation(): Promise<ContentItem | null> {
  return getContent('insurer_regulations');
}

export interface MinimumEthicsBanner {
  image: string;
  url: string;
}

export async function getMinimumEthicsBanner(): Promise<MinimumEthicsBanner | null> {
  const item = await getContent('minimum_ethics_banner');
  if (!item || !item.content) {
    return null;
  }
  try {
    const data = JSON.parse(item.content) as Partial<MinimumEthicsBanner>;
    if (!data.image || !data.url) {
      return null;
    }
    return { image: data.image, url: data.url };
  } catch {
    return null;
  }
}

export interface News {
  id: number;
  title: string;
  content: string;
  createdAt: string;
  updatedAt: string;
}

export async function getNews(): Promise<News[]> {
  const url = new URL(`${ config.baseUrl }/contents/news`);

  const response = await fetch(url.toString(), {
    method: 'GET',
    headers: {
      'Content-Type': 'application/json',
      Authorization: getCookie('accessToken'),
    },
  });

  if (response.status !== 200) {
    console.error(response.statusText);
    throw new Error('Ocurrió un error al recuperar las novedades.');
  }

  const content = await response.json() as APIResponse<News[]>;
  if (content.error || ! content.payload) {
    console.error(content.error);
    throw new Error('Ocurrió un error al recuperar las novedades.');
  }

  return content.payload;
}

export interface Link {
  id: number;
  order: number;
  name: string;
  description: string | null;
  url: string;
  section: string;
}

export async function getLinks(section: string = 'home'): Promise<Link[]> {
  const url = new URL(`${ config.baseUrl }/contents/links`);
  url.searchParams.set('section', section);

  const response = await fetch(url.toString(), {
    method: 'GET',
    headers: {
      'Content-Type': 'application/json',
      Authorization: getCookie('accessToken'),
    },
  });

  if (response.status !== 200) {
    console.error(response.statusText);
    throw new Error('Ocurrió un error al recuperar los enlaces.');
  }

  const content = await response.json() as APIResponse<Link[]>;
  if (content.error || !content.payload) {
    console.error(content.error);
    throw new Error('Ocurrió un error al recuperar los enlaces.');
  }

  return content.payload;
}
